import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Menu, IndianRupee, TrendingUp, ArrowUpRight, Grid, Calendar, Calendar as CalendarIcon } from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, Sector, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { COLORS } from '../utils/constants';

export default function Analytics({ incomeData, toggleSidebar, isDark }) {
  const [timeRange, setTimeRange] = useState('all');
  const [chartType, setChartType] = useState('area');
  const [pieIndex, setPieIndex] = useState(-1);

  const { metrics, chartData, cumulativeData, weekdayData, sourceData } = useMemo(() => {
    if (!incomeData || incomeData.length === 0) {
      return {
        currentData: [], metrics: { total: 0, delta: 0, avg: 0, label: "No Data" },
        chartData: [], cumulativeData: [], weekdayData: [], sourceData: []
      };
    }

    const sorted = [...incomeData].sort((a, b) => new Date(a.date) - new Date(b.date));
    const now = new Date();
    let filtered = sorted;
    let label = "All Time";

    if (timeRange === '7d') {
      const cutoff = new Date(); cutoff.setDate(now.getDate() - 7);
      filtered = sorted.filter(i => new Date(i.date) >= cutoff); label = "Last 7 Days";
    } else if (timeRange === '30d') {
      const cutoff = new Date(); cutoff.setDate(now.getDate() - 30);
      filtered = sorted.filter(i => new Date(i.date) >= cutoff); label = "Last 30 Days";
    } else if (timeRange === '90d') {
      const cutoff = new Date(); cutoff.setDate(now.getDate() - 90);
      filtered = sorted.filter(i => new Date(i.date) >= cutoff); label = "Last 3 Months";
    }

    const total = filtered.reduce((sum, i) => sum + i.amount, 0);
    const avg = filtered.length ? Math.round(total / filtered.length) : 0;
    const mid = Math.floor(filtered.length / 2);
    const prev = filtered.slice(0, mid).reduce((s, i) => s + i.amount, 0);
    const curr = filtered.slice(mid).reduce((s, i) => s + i.amount, 0);
    const delta = prev > 0 ? ((curr - prev) / prev) * 100 : 0;

    const map = {}; filtered.forEach(i => { map[i.date] = (map[i.date] || 0) + i.amount; });
    const cData = Object.keys(map).map(d => ({ date: d, amount: map[d] }));

    let runningTotal = 0;
    const accData = cData.map(item => { runningTotal += item.amount; return { date: item.date, total: runningTotal }; });

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weekMap = new Array(7).fill(0);
    filtered.forEach(i => weekMap[new Date(i.date).getDay()] += i.amount);
    const wData = days.map((d, i) => ({ day: d, amount: weekMap[i] }));

    const sMap = {}; filtered.forEach(i => sMap[i.source] = (sMap[i.source] || 0) + i.amount);
    const sData = Object.keys(sMap).map(name => ({ name, value: sMap[name] })).sort((a, b) => b.value - a.value);

    return { currentData: filtered, metrics: { total, delta, avg, label }, chartData: cData, cumulativeData: accData, weekdayData: wData, sourceData: sData };
  }, [incomeData, timeRange]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="modern-tooltip">
          <div className="tooltip-glow"></div>
          <p className="tooltip-lbl">{label}</p>
          <p className="tooltip-val">₹{payload[0].value.toLocaleString()}</p>
        </div>
      );
    }
    return null;
  };

  const renderActiveDonut = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } = props;
    return (
      <g>
        <Sector
          cx={cx} cy={cy}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 8}
          startAngle={startAngle} endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx} cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle} endAngle={endAngle}
          fill={fill}
          style={{ filter: `drop-shadow(0 0 8px ${fill})` }}
        />

        <text x={cx} y={cy} dy={-15} textAnchor="middle" fill={isDark ? "#FFF" : "#0F172A"} fontSize={22} fontWeight={800}>
          {((value / metrics.total) * 100).toFixed(0)}%
        </text>
        <text x={cx} y={cy} dy={15} textAnchor="middle" fill={isDark ? "#94A3B8" : "#64748B"} fontSize={14} fontWeight={600}>
          {payload.name}
        </text>
      </g>
    );
  };

  return (
    <motion.div className="page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="page-header-enhanced">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="mobile-menu-btn" onClick={toggleSidebar}><Menu size={24} /></button>
          <div>
            <h1 className="page-title">Analytics</h1>
            <p className="page-subtitle">{metrics.label} Overview</p>
          </div>
        </div>
        <div className="glass-pills">
          <button
            className={`pill-btn ${timeRange === 'all' ? 'active' : ''}`}
            data-label="All Time"
            onClick={() => setTimeRange('all')}
          >
            <Grid size={16} />
            <span className="pill-text">All</span>
          </button>

          <button
            className={`pill-btn ${timeRange === '7d' ? 'active' : ''}`}
            data-label="Last 7 Days"
            onClick={() => setTimeRange('7d')}
          >
            <CalendarIcon size={16} />
            <span className="pill-text">7D</span>
          </button>

          <button
            className={`pill-btn ${timeRange === '30d' ? 'active' : ''}`}
            data-label="Last 30 Days"
            onClick={() => setTimeRange('30d')}
          >
            <Calendar size={16} />
            <span className="pill-text">1M</span>
          </button>
        </div>

      </div>

      <div className="stats-grid-fixed">
        <div className="stat-card-fixed primary">
          <div className="stat-icon-large"><IndianRupee size={28} /></div>
          <div><p className="stat-label-large">Revenue</p><h3 className="stat-value-huge">₹{metrics.total.toLocaleString()}</h3></div>
        </div>
        <div className="stat-card-fixed">
          <div className="stat-icon-large secondary"><TrendingUp size={24} /></div>
          <div><p className="stat-label-large">Avg Entry</p><h3 className="stat-value-large">₹{metrics.avg.toLocaleString()}</h3></div>
        </div>
        <div className="stat-card-fixed tertiary">
          <div className="stat-icon-large"><ArrowUpRight size={24} /></div>
          <div>
            <p className="stat-label-large">Net Growth</p>
            <h3 className={`stat-value-large ${metrics.delta >= 0 ? 'txt-green' : 'txt-red'}`}>
              {metrics.delta > 0 ? '+' : ''}{metrics.delta.toFixed(1)}%
            </h3>
          </div>
        </div>
      </div>

      <div className="analytics-grid">

        <div className="chart-card-full">
          <div className="chart-header-large">
            <h3>Income Flow</h3>
            <div className="toggle-tiny">
              <button onClick={() => setChartType('area')} className={chartType === 'area' ? 'on' : ''}>Curve</button>
              <button onClick={() => setChartType('bar')} className={chartType === 'bar' ? 'on' : ''}>Bar</button>
            </div>
          </div>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              {chartType === 'area' ? (
                <AreaChart data={chartData}>
                  <defs>
                    <filter id="neonGlow" height="200%" width="200%" x="-50%" y="-50%">
                      <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                      <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <linearGradient id="colorGlow" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.1)" vertical={false} />
                  <XAxis dataKey="date" stroke="#64748B" tickLine={false} axisLine={false} dy={10} tickFormatter={d => d.split('-')[2]} />
                  <YAxis stroke="#64748B" tickLine={false} axisLine={false} tickFormatter={v => `₹${v / 1000}k`} />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#3B82F6', strokeWidth: 1, strokeDasharray: '4 4' }} />
                  <Area
                    type="monotone" dataKey="amount"
                    stroke="#3B82F6" strokeWidth={3}
                    fill="url(#colorGlow)"
                    style={{ filter: 'url(#neonGlow)' }}
                    animationDuration={1500}
                  />
                </AreaChart>
              ) : (
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.1)" vertical={false} />
                  <XAxis dataKey="date" stroke="#64748B" tickLine={false} axisLine={false} dy={10} tickFormatter={d => d.split('-')[2]} />
                  <YAxis stroke="#64748B" tickLine={false} axisLine={false} tickFormatter={v => `₹${v / 1000}k`} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                  <defs>
                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="100%" stopColor="#1E40AF" />
                    </linearGradient>
                  </defs>
                  <Bar dataKey="amount" fill="url(#barGrad)" radius={[4, 4, 0, 0]} animationDuration={1000} style={{ filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.3))' }} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card-half">
          <div className="chart-header-large"><h3>Growth Trajectory</h3></div>
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
              <AreaChart data={cumulativeData}>
                <defs>
                  <linearGradient id="colorGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.1)" vertical={false} />
                <XAxis dataKey="date" hide />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" stroke="#F59E0B" strokeWidth={3} fill="url(#colorGold)" animationDuration={2000} style={{ filter: 'drop-shadow(0 0 8px rgba(245, 158, 11, 0.4))' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card-half">
          <div className="chart-header-large"><h3>Daily Volume</h3></div>
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
              <BarChart data={weekdayData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.1)" vertical={false} />
                <XAxis dataKey="day" stroke="#64748B" tickLine={false} axisLine={false} dy={10} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="amount" radius={[6, 6, 0, 0]} animationDuration={1200} fill="#10B981" style={{ filter: 'drop-shadow(0 0 5px rgba(16, 185, 129, 0.4))' }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card-half">
          <div className="chart-header-large"><h3>Income Streams</h3></div>
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  activeIndex={pieIndex}
                  activeShape={renderActiveDonut}
                  data={sourceData}
                  cx="50%" cy="50%"
                  innerRadius={70}
                  outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                  onMouseEnter={(_, index) => setPieIndex(index)}
                  onMouseLeave={() => setPieIndex(-1)}
                >
                  {sourceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card-half">
          <div className="chart-header-large"><h3>Details</h3></div>
          <div className="source-list-scroll">
            {sourceData.map((item, index) => (
              <div key={index} className="source-row-item">
                <div className="s-icon" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                <span className="s-name">{item.name}</span>
                <span className="s-val">₹{item.value.toLocaleString()}</span>
                <span className="s-perc">
                  {((item.value / metrics.total) * 100).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </motion.div>
  );
}
